import { useRef } from 'react';
import { ExpandIcon } from 'lucide-react';

import { DownloadAttachmentIcon } from 'shared/assets/svg/menu-icons/DownloadAttachmentIcon';
import { FinecoPaperClipIcon } from 'shared/assets/svg/FinecoPaperClipIcon';

import { ImageFileArtifact } from './ImageFileArtifact';
import { CsvFileArtifact } from './CsvFileArtifact';
import { SvgFileArtifact } from './SvgFileArtifact';
import { TxtFileArtifact } from './TxtFileArtifact';
import { DownloadableArtifact } from './DownloadableArtifact';

import type { File } from '@dev-streetbeat/chat';

import styles from './FileArtifact.module.scss';

type Props = {
  file: File;
};
type SvgAttachmentRef = {
  expandSvg: () => void;
  downloadSvg: () => void;
};

export const FileArtifact: React.FC<Props> = ({ file }) => {
  const dialogRef = useRef<SvgAttachmentRef>(null);
  let attachment = null;
  if (file.type.startsWith('image/svg')) {
    attachment = <SvgFileArtifact file={file} />;
  } else if (file.type.startsWith('image/')) {
    attachment = <ImageFileArtifact file={file} />;
  } else if (file.type === 'text/csv') {
    attachment = <CsvFileArtifact file={file} />;
  } else if (file.type === 'text/plain') {
    attachment = <TxtFileArtifact file={file} />;
  } else {
    attachment = <DownloadableArtifact file={file} />;
  }

  // Extract file extension
  let fileExtension = file.name.split('.').pop();
  const attachmentNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
  if (fileExtension === 'csv') {
    fileExtension = 'xlsx';
  }
  return (
    <>
      {file.type !== 'image/svg' && file.type !== 'application/pdf' && (
        <div className={styles.attachmentResponseContainer}>
          <div className={styles.attachmentDetailsWrapper}>
            <div className={styles.attachmentIcon}>
              <FinecoPaperClipIcon className="size-4 text-gray-600" />
            </div>
            <div className={styles.attachmentDetails}>
              <div className={styles.fileName}>{attachmentNameWithoutExtension}</div>
              <div className={styles.fileInfo}>{fileExtension?.toUpperCase()}</div>
            </div>
          </div>
          <div className={styles.attachmentActions}>{attachment}</div>
        </div>
      )}
      {file.type === 'application/pdf' && (
        <div className={styles.attachmentResponseContainerPDF}>
          <div className={styles.attachmentDetailsWrapper}>
            <div className={styles.attachmentIconOuter}>
              <div className={styles.attachmentIcon}>
                <FinecoPaperClipIcon className={styles.attachmentIconInner} />
              </div>
            </div>
            <div className={styles.attachmentDetails}>
              <div className={styles.fileName}>{attachmentNameWithoutExtension}</div>
              <div className={styles.fileInfo}>{fileExtension?.toUpperCase()}</div>
            </div>
          </div>
          <div className={styles.attachmentActions}>{attachment}</div>
        </div>
      )}
      {file.type === 'image/svg' && (
        <div className={styles.attachmentResponseContainerSVG}>
          <div className={styles.attachmentHeader}>
            <div className={styles.btContainer}>
              <div className={styles.attachmentIcon}>
                <FinecoPaperClipIcon className="size-4 text-gray-600" />
              </div>
              <div className={styles.attachmentDetails}>
                <div className={styles.fileName}>{attachmentNameWithoutExtension}</div>
                <div className={styles.fileInfo}>{fileExtension?.toUpperCase()}</div>
              </div>
            </div>
            <div className={styles.attachmentActions}>
              <div
                className={styles.iconStyle}
                onClick={() => {
                  dialogRef.current?.downloadSvg();
                }}
              >
                <DownloadAttachmentIcon width={30} height={30} viewBox="3 3 24 24" />
              </div>
              <div
                className={styles.iconStyle}
                onClick={() => {
                  dialogRef.current?.expandSvg();
                }}
              >
                <ExpandIcon width={30} height={30} viewBox="-4 -4 32 32" strokeWidth={1.5} />
              </div>
            </div>
          </div>
          <SvgFileArtifact ref={dialogRef} file={file} />
        </div>
      )}
    </>
  );
};
